import { useState } from "react";
import { KanfyLogo } from "@/components/KanfyLogo";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import {
  Mail,
  Phone,
  MapPin,
  ChevronDown,
  Globe
} from "lucide-react";
import { useNavigate } from 'react-router';
import { useLanguage } from "@/hooks/useLanguage";
import { useTranslation } from "@/hooks/useTranslation";
import { useDynamicCategories } from "@/hooks/useDynamicCategories";
import { useCountryConfig } from "@/lib/countryConfig";
import { useContactSettings } from '@/hooks/useContactSettings';
import { cn } from "@/lib/utils";
import { isNative } from '@/lib/env';

/** No footer in the native shell: the tab bar and Account carry its links. */
export function Footer() {
  return isNative ? null : <WebFooter />;
}

function WebFooter() {
  const navigate = useNavigate();
  const { config } = useCountryConfig();
  const { contact } = useContactSettings();
  const { language, setLanguage, getLanguageLabel } = useLanguage();
  const { t } = useTranslation();
    const { popularCategories } = useDynamicCategories();
  const [openSection, setOpenSection] = useState<string | null>(null);

  const toggleSection = (section: string) => {
    setOpenSection(prev => prev === section ? null : section);
  };

// Dynamic: sourced from the admin Categories panel (service_categories table)
      // via useDynamicCategories, instead of a hardcoded list, so these links
      // always match whatever services are currently enabled for provision.
      const services = popularCategories.map((cat) => ({
              name: cat.name,
              route: `/services/${cat.slug}`,
      }));

  const aboutLinks = [
    { name: "About Us", route: "/about" },
    { name: "How It Works", route: "/how-it-works" },
    { name: "Become a Helper", route: "/become-helper" },
    { name: "Blog", route: "/blog" },
    { name: "Careers", route: "/careers" }
  ];

  const resourceLinks = [
    { name: "FAQ", route: "/faq" },
    { name: "Safety Tips", route: "/safety" },
    { name: "Service Standards", route: "/service-standards" },
    { name: "Care Guides", route: "/guides" },
    { name: "Helper Resources", route: "/helper-resources" },
    { name: "Support Center", route: "/support" }
  ];

  const legalLinks = [
    { name: "Terms of Service", route: "/terms" },
    { name: "Privacy Policy", route: "/privacy" },
    { name: "Cookie Policy", route: "/cookies" },
    { name: "Community Guidelines", route: "/guidelines" },
    { name: "Platform Rules", route: "/platform-rules" },
    { name: "Delete My Data", route: "/data-deletion" }
  ];

  const LinkList = ({ links }: { links: { name: string; route: string }[] }) => (
    <ul className="grid grid-cols-2 gap-x-3 gap-y-0 pb-2 md:block md:space-y-2">
      {links.map((link) => (
        <li key={link.name}>
          <button
            onClick={() => navigate(link.route)}
            className="text-[13px] md:text-sm text-muted-foreground hover:text-primary transition-colors min-h-[36px] md:min-h-[44px] flex items-center text-left leading-tight"
          >
            {link.name}
          </button>
        </li>
      ))}
    </ul>
  );

  const AccordionSection = ({
    id,
    title,
    children,
  }: {
    id: string;
    title: string;
    children: React.ReactNode;
  }) => (
    <div className="border-t md:border-0">
      {/* Mobile: collapsible toggle */}
      <button
        className="md:hidden w-full flex items-center justify-between py-3 font-semibold text-foreground min-h-[44px]"
        onClick={() => toggleSection(id)}
        aria-expanded={openSection === id}
      >
        {title}
        <ChevronDown
          className={cn(
            "h-4 w-4 text-muted-foreground transition-transform duration-200",
            openSection === id && "rotate-180"
          )}
        />
      </button>
      {/* Desktop: always visible heading — teal colour */}
      <h4 className="hidden md:block font-semibold text-primary mb-4">{title}</h4>
      {/* Content: visible always on md+, conditionally on mobile */}
      <div className={cn("md:block", openSection === id ? "block" : "hidden")}>
        {children}
      </div>
    </div>
  );

  return (
    <footer className="bg-background border-t">
      {/* BottomNav clearance comes from the global body padding rule (#441) */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-8 md:pb-12">

        {/* Main Footer Content */}
        <div className="mb-6">
          {/* Company Info — always full width on mobile */}
          <div className="mb-6 pb-4 border-b md:border-0 md:hidden">
            <div className="flex items-center gap-2 mb-3">
              <KanfyLogo variant="reversed" size="sm" knockout="#0D3A31" />
            </div>
            <p className="text-muted-foreground text-sm mb-4">{t('companyDescription')}</p>
          </div>

          {/* Desktop: 5-column grid */}
          <div className="hidden md:grid md:grid-cols-3 lg:grid-cols-5 gap-8">
            {/* Company Info — desktop only */}
            <div className="lg:col-span-1">
              <div className="flex items-center gap-2 mb-4">
                <KanfyLogo variant="reversed" size="sm" knockout="#0D3A31" />
              </div>
              <p className="text-muted-foreground text-sm mb-5">{t('companyDescription')}</p>
            </div>
            <div>
              <h4 className="font-semibold text-primary mb-4">{t('services')}</h4>
              <LinkList links={services} />
            </div>
            <div>
              <h4 className="font-semibold text-primary mb-4">{t('about')}</h4>
              <LinkList links={aboutLinks} />
            </div>
            <div>
              <h4 className="font-semibold text-primary mb-4">{t('resources')}</h4>
              <LinkList links={resourceLinks} />
            </div>
            <div>
              <h4 className="font-semibold text-primary mb-4">{t('legal')}</h4>
              <LinkList links={legalLinks} />
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="w-full mt-4 border-border text-foreground bg-transparent hover:bg-muted hover:text-foreground">
                    <Globe className="h-4 w-4 mr-2" />
                    {getLanguageLabel(language)}
                    <ChevronDown className="h-4 w-4 ml-2" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="bg-background border border-border shadow-lg">
                  {[
                    { code: 'en-UG', label: '🇺🇬 English (UG)' },
                    { code: 'en-US', label: '🇺🇸 English (US)' },
                    { code: 'fr', label: '🇫🇷 French' },
                    { code: 'sw', label: '🇰🇪 Swahili' },
                    { code: 'ar', label: '🇸🇦 Arabic' },
                  ].map(({ code, label }) => (
                    <DropdownMenuItem
                      key={code}
                      className="hover:bg-accent hover:text-accent-foreground cursor-pointer"
                      onClick={() => setLanguage(code as Parameters<typeof setLanguage>[0])}
                    >
                      {label}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          {/* Mobile: accordion sections */}
          <div className="md:hidden">
            <AccordionSection id="services" title={t('services')}>
              <LinkList links={services} />
            </AccordionSection>
            <AccordionSection id="about" title={t('about')}>
              <LinkList links={aboutLinks} />
            </AccordionSection>
            <AccordionSection id="resources" title={t('resources')}>
              <LinkList links={resourceLinks} />
            </AccordionSection>
            <AccordionSection id="legal" title={t('legal')}>
              <LinkList links={legalLinks} />
            </AccordionSection>
            {/* Language on mobile */}
            <div className="border-t pt-3 pb-2">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="w-full border-border text-foreground bg-transparent hover:bg-muted hover:text-foreground">
                    <Globe className="h-4 w-4 mr-2" />
                    {getLanguageLabel(language)}
                    <ChevronDown className="h-4 w-4 ml-2" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="bg-background border border-border shadow-lg">
                  {[
                    { code: 'en-UG', label: '🇺🇬 English (UG)' },
                    { code: 'en-US', label: '🇺🇸 English (US)' },
                    { code: 'fr', label: '🇫🇷 French' },
                    { code: 'sw', label: '🇰🇪 Swahili' },
                    { code: 'ar', label: '🇸🇦 Arabic' },
                  ].map(({ code, label }) => (
                    <DropdownMenuItem
                      key={code}
                      className="hover:bg-accent hover:text-accent-foreground cursor-pointer"
                      onClick={() => setLanguage(code as Parameters<typeof setLanguage>[0])}
                    >
                      {label}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>

        {/* Contact Info */}
        <div className="border-t pt-4 mb-4 md:pt-6 md:mb-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="flex items-center gap-2">
              <Mail className="h-4 w-4 text-primary shrink-0" />
              <a href={`mailto:${contact.support_email}`} className="text-sm text-muted-foreground break-all hover:text-primary">{contact.support_email}</a>
            </div>
            {contact.support_phone && (
              <div className="flex items-center gap-2">
                <Phone className="h-4 w-4 text-primary shrink-0" />
                <a href={`tel:${contact.support_phone.replace(/\s/g, '')}`} className="text-sm text-muted-foreground hover:text-primary">{contact.support_phone}</a>
              </div>
            )}
            <div className="flex items-center gap-2">
              <MapPin className="h-4 w-4 text-primary shrink-0" />
              <span className="text-sm text-muted-foreground">{config.support.location}</span>
            </div>
          </div>
        </div>

        {/* Regulatory + Payment Methods */}
        <div className="border-t pt-4 mb-4 md:pt-5 md:mb-5">
          {/* Regulatory text — wraps cleanly */}
          <p className="text-xs text-muted-foreground leading-relaxed mb-4">
            <span className="inline-flex items-center gap-1 mr-2">
              <span className="inline-block w-2 h-2 rounded-full bg-trust shrink-0" />
              Reg. No. 80020002576789 · URSB
            </span>
            <span className="mx-1 hidden sm:inline text-border">·</span>
            Licensed · {config.legal.regulator}
            <span className="mx-1 hidden sm:inline text-border">·</span>
            NITA-U Compliant
          </p>

          {/* Payment badges — centered row on mobile */}
          <div className="flex flex-wrap justify-center sm:justify-start items-center gap-2">
            <span className="text-xs text-muted-foreground">We accept:</span>
            {[
              { label: 'MTN MoMo', color: 'bg-yellow-400 text-yellow-900' },
              { label: 'Airtel Money', color: 'bg-red-600 text-white' },
              { label: 'Visa', color: 'bg-blue-700 text-white' },
              { label: 'Mastercard', color: 'bg-orange-700 text-white' },
            ].map(({ label, color }) => (
              <span
                key={label}
                className={`${color} text-[10px] font-bold px-2 py-1 rounded`}
              >
                {label}
              </span>
            ))}
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t pt-5">
          <div className="flex flex-col sm:flex-row justify-between items-center text-xs text-muted-foreground gap-2">
            <p>© {new Date().getFullYear()} Kanfy {config.name} Ltd. All rights reserved.</p>
            <p>{t('tagline')}</p>
          </div>
        </div>
      </div>
    </footer>
  );
}
